import { useCallback, useEffect, useMemo, useState } from "react";
import { FaHistory, FaSearch, FaSyncAlt, FaWarehouse } from "react-icons/fa";
import api from "../api";
import toast from "../utils/toast";
import "./Inventario.css";

const BASE = "/api/insumos";
const UNIDADES_FALLBACK = ["kg", "g", "litro", "ml", "unidad", "paquete", "caja", "otro"];

const ESTADOS = {
  sin_stock: { label: "Sin stock", className: "inv-status--danger" },
  ok: { label: "Disponible", className: "inv-status--ok" },
  inactivo: { label: "Inactivo", className: "inv-status--muted" },
};

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatCantidad(value) {
  return new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(toNumber(value));
}

function getEstado(item) {
  if (!item.activo) return "inactivo";
  const stock = toNumber(item.stock_actual);
  if (stock <= 0) return "sin_stock";
  return "ok";
}

export default function Inventario() {
  const [insumos, setInsumos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const [estado, setEstado] = useState("todos");
  const [visibilidad, setVisibilidad] = useState("activos");
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(20);
  const [movimiento, setMovimiento] = useState(null);
  const [cantidadMovimiento, setCantidadMovimiento] = useState("");
  const [retiradoPor, setRetiradoPor] = useState("");
  const [guardandoMovimiento, setGuardandoMovimiento] = useState(false);
  const [historial, setHistorial] = useState(null);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [productoModal, setProductoModal] = useState(false);
  const [guardandoProducto, setGuardandoProducto] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [categoriasCatalogo, setCategoriasCatalogo] = useState([]);
  const [unidades, setUnidades] = useState(UNIDADES_FALLBACK);
  const [productoForm, setProductoForm] = useState({
    nombre: "",
    categoria_id: "",
    unidad_medida: "unidad",
    unidad_personalizada: "",
    stock_actual: "",
    activo: true,
  });

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      setError("");
      const { data } = await api.get(`${BASE}/insumos`);
      setInsumos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo cargar inventario");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cargarCatalogos = useCallback(async () => {
    try {
      const [rCat, rUni] = await Promise.all([
        api.get(`${BASE}/categorias`),
        api.get(`${BASE}/unidades`),
      ]);
      setCategoriasCatalogo(Array.isArray(rCat.data) ? rCat.data : []);
      setUnidades(Array.isArray(rUni.data) && rUni.data.length > 0 ? rUni.data : UNIDADES_FALLBACK);
    } catch (_) {
      setCategoriasCatalogo([]);
      setUnidades(UNIDADES_FALLBACK);
    }
  }, []);

  useEffect(() => {
    cargarCatalogos();
  }, [cargarCatalogos]);

  const categorias = useMemo(() => {
    return Array.from(
      new Set([
        ...insumos.map((i) => i.categoria_nombre).filter(Boolean),
        ...categoriasCatalogo.map((c) => c.nombre).filter(Boolean),
      ])
    ).sort((a, b) => a.localeCompare(b, "es"));
  }, [insumos, categoriasCatalogo]);

  const filas = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    return insumos
      .map((item) => ({ ...item, estadoInventario: getEstado(item) }))
      .filter((item) => {
        if (visibilidad === "activos" && !item.activo) return false;
        if (visibilidad === "inactivos" && item.activo) return false;
        if (categoria !== "todas" && (item.categoria_nombre || "Sin categoria") !== categoria) return false;
        if (estado !== "todos" && item.estadoInventario !== estado) return false;
        if (!term) return true;
        return `${item.nombre || ""} ${item.categoria_nombre || ""} ${item.unidad_medida || ""}`
          .toLowerCase()
          .includes(term);
      })
      .sort((a, b) => {
        const estadoOrder = { sin_stock: 0, ok: 1, inactivo: 2 };
        const byEstado = estadoOrder[a.estadoInventario] - estadoOrder[b.estadoInventario];
        if (byEstado !== 0) return byEstado;
        return (a.nombre || "").localeCompare(b.nombre || "", "es");
      });
  }, [insumos, busqueda, categoria, estado, visibilidad]);

  const totalPaginas = Math.max(1, Math.ceil(filas.length / itemsPorPagina));
  const inicioPagina = filas.length === 0 ? 0 : (paginaActual - 1) * itemsPorPagina + 1;
  const finPagina = Math.min(filas.length, paginaActual * itemsPorPagina);

  const filasPaginadas = useMemo(() => {
    const inicio = (paginaActual - 1) * itemsPorPagina;
    return filas.slice(inicio, inicio + itemsPorPagina);
  }, [filas, paginaActual, itemsPorPagina]);

  useEffect(() => {
    setPaginaActual(1);
  }, [busqueda, categoria, estado, visibilidad, itemsPorPagina]);

  useEffect(() => {
    setPaginaActual((pagina) => Math.min(Math.max(1, pagina), totalPaginas));
  }, [totalPaginas]);

  const resumen = useMemo(() => {
    const activos = insumos.filter((i) => i.activo);
    const unidades = new Map();
    let sinStock = 0;

    for (const item of activos) {
      const estadoItem = getEstado(item);
      if (estadoItem === "sin_stock") sinStock += 1;
      const unidad = item.unidad_medida || "unidad";
      unidades.set(unidad, (unidades.get(unidad) || 0) + toNumber(item.stock_actual));
    }

    return {
      activos: activos.length,
      sinStock,
      categorias: new Set(activos.map((i) => i.categoria_nombre).filter(Boolean)).size,
      unidades: Array.from(unidades.entries()).sort((a, b) => a[0].localeCompare(b[0], "es")),
    };
  }, [insumos]);

  const limpiarFiltros = () => {
    setBusqueda("");
    setCategoria("todas");
    setEstado("todos");
    setVisibilidad("activos");
  };

  const abrirMovimiento = async (item, tipo) => {
    setMovimiento({ item, tipo });
    setCantidadMovimiento("");
    setRetiradoPor("");
  };

  const cerrarMovimiento = () => {
    if (!guardandoMovimiento) setMovimiento(null);
  };

  const guardarMovimiento = async (e) => {
    e.preventDefault();
    if (!movimiento) return;
    const cantidad = Number(cantidadMovimiento);
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      toast("La cantidad debe ser mayor a 0");
      return;
    }
    if (movimiento.tipo === "SALIDA" && !retiradoPor.trim()) {
      toast("Debes indicar quién retiró el producto");
      return;
    }
    try {
      setGuardandoMovimiento(true);
      const { data } = await api.post(`${BASE}/movimientos`, {
        insumo_id: movimiento.item.id,
        tipo: movimiento.tipo,
        cantidad,
        retirado_por: movimiento.tipo === "SALIDA" ? retiradoPor.trim() : null,
      });
      setInsumos((actuales) => actuales.map((item) => item.id === movimiento.item.id
        ? { ...item, stock_actual: data.stock_resultante }
        : item));
      setMovimiento(null);
      toast("Movimiento registrado correctamente", "exito");
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo registrar el movimiento");
    } finally {
      setGuardandoMovimiento(false);
    }
  };

  const abrirHistorial = async (item) => {
    setHistorial({ item, movimientos: [] });
    setCargandoHistorial(true);
    try {
      const { data } = await api.get(`${BASE}/movimientos`, { params: { insumo_id: item.id } });
      setHistorial({ item, movimientos: Array.isArray(data) ? data : [] });
    } catch (e) {
      setHistorial(null);
      toast(e?.response?.data?.error || "No se pudo cargar el historial");
    } finally {
      setCargandoHistorial(false);
    }
  };

  const guardarProducto = async (e) => {
    e.preventDefault();
    const nombre = productoForm.nombre.trim();
    if (!nombre) return toast("El nombre del insumo es obligatorio");

    const stockActual = Number(productoForm.stock_actual || 0);
    if (!Number.isFinite(stockActual) || stockActual < 0) {
      return toast("El stock actual no puede ser negativo");
    }
    const unidadMedida = productoForm.unidad_medida === "otro"
      ? productoForm.unidad_personalizada.trim()
      : productoForm.unidad_medida;
    if (!unidadMedida) {
      return toast("Debes indicar la unidad");
    }

    try {
      setGuardandoProducto(true);
      await api.post(`${BASE}/insumos`, {
        nombre,
        categoria_id: productoForm.categoria_id || null,
        unidad_medida: unidadMedida,
        stock_actual: stockActual,
        activo: productoForm.activo,
      });
      setProductoForm({
        nombre: "",
        categoria_id: "",
        unidad_medida: "unidad",
        unidad_personalizada: "",
        stock_actual: "",
        activo: true,
      });
      setProductoModal(false);
      await cargar();
      toast("Insumo agregado correctamente", "exito");
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo agregar el insumo");
    } finally {
      setGuardandoProducto(false);
    }
  };

  const eliminarInsumo = async (item) => {
    if (!item?.id || eliminandoId) return;
    if (!window.confirm(`Eliminar insumo "${item.nombre}" del inventario activo?`)) return;

    try {
      setEliminandoId(item.id);
      await api.delete(`${BASE}/insumos/${item.id}`);
      setInsumos((actuales) => actuales.map((insumo) => (
        insumo.id === item.id ? { ...insumo, activo: false } : insumo
      )));
      toast("Insumo eliminado correctamente", "exito");
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo eliminar el insumo");
    } finally {
      setEliminandoId(null);
    }
  };

  const formatearFecha = (value) => new Intl.DateTimeFormat("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));

  return (
    <div className="inv-page">
      <header className="inv-header">
        <div>
          <div className="inv-title-row">
            <FaWarehouse aria-hidden="true" />
            <h1>Inventario</h1>
          </div>
          <p>Conteo actual de insumos registrados en el local.</p>
        </div>
        <div className="inv-header-actions">
          <button type="button" className="inv-add-product-btn" onClick={() => setProductoModal(true)}>Agregar insumo</button>
          <button type="button" className="inv-icon-btn" onClick={cargar} title="Actualizar inventario" disabled={cargando}>
            <FaSyncAlt aria-hidden="true" />
            <span>Actualizar</span>
          </button>
        </div>
      </header>

      <section className="inv-summary" aria-label="Resumen de inventario">
        <div className="inv-metric">
          <span className="inv-metric__label">Insumos activos</span>
          <strong>{resumen.activos}</strong>
        </div>
        <div className="inv-metric inv-metric--danger">
          <span className="inv-metric__label">Sin stock</span>
          <strong>{resumen.sinStock}</strong>
        </div>
        <div className="inv-metric">
          <span className="inv-metric__label">Categorias</span>
          <strong>{resumen.categorias}</strong>
        </div>
      </section>

      <section className="inv-units" aria-label="Totales por unidad">
        <span className="inv-units__title">Totales por unidad</span>
        <div className="inv-units__list">
          {resumen.unidades.length > 0 ? (
            resumen.unidades.map(([unidad, total]) => (
              <span key={unidad} className="inv-unit-pill">
                {formatCantidad(total)} {unidad}
              </span>
            ))
          ) : (
            <span className="inv-muted">Sin stock registrado</span>
          )}
        </div>
      </section>

      <section className="inv-panel">
        <div className="inv-toolbar">
          <label className="inv-search">
            <FaSearch aria-hidden="true" />
            <input
              type="search"
              placeholder="Buscar insumo, categoria o unidad"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </label>

          <div className="inv-filters">
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} aria-label="Filtrar por categoria">
              <option value="todas">Todas las categorias</option>
              {categorias.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <select value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Filtrar por estado">
              <option value="todos">Todos los estados</option>
              <option value="sin_stock">Sin stock</option>
              <option value="ok">Disponible</option>
              <option value="inactivo">Inactivo</option>
            </select>
            <select value={visibilidad} onChange={(e) => setVisibilidad(e.target.value)} aria-label="Filtrar por estado activo">
              <option value="activos">Solo activos</option>
              <option value="todos">Todos</option>
              <option value="inactivos">Solo inactivos</option>
            </select>
            <button type="button" className="inv-clear-btn" onClick={limpiarFiltros}>Limpiar</button>
          </div>
        </div>

        {error && <div className="inv-alert">Error: {error}</div>}

        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th>Insumo</th>
                <th>Categoria</th>
                <th>Stock actual</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargando ? (
                <tr>
                  <td colSpan={5} className="inv-empty">Cargando inventario...</td>
                </tr>
              ) : filas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="inv-empty">No hay insumos para mostrar.</td>
                </tr>
              ) : (
                filasPaginadas.map((item) => {
                  const stock = toNumber(item.stock_actual);
                  const estadoInfo = ESTADOS[getEstado({ ...item, stock_actual: stock })] || ESTADOS.ok;

                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="inv-name">{item.nombre}</div>
                        <div className="inv-sub">{item.unidad_medida || "unidad"}</div>
                      </td>
                      <td>{item.categoria_nombre || "Sin categoria"}</td>
                      <td className="inv-number">{formatCantidad(stock)} {item.unidad_medida || "unidad"}</td>
                      <td>
                        <span className={`inv-status ${estadoInfo.className}`}>{estadoInfo.label}</span>
                      </td>
                      <td className="inv-actions">
                        <button type="button" className="inv-action-btn" onClick={() => abrirMovimiento(item, "ENTRADA")}>Ingresar stock</button>
                        <button type="button" className="inv-action-btn inv-action-btn--out" onClick={() => abrirMovimiento(item, "SALIDA")}>Registrar salida</button>
                        <button type="button" className="inv-action-btn inv-action-btn--history" onClick={() => abrirHistorial(item)} title="Ver historial">
                          <FaHistory aria-hidden="true" /> Historial
                        </button>
                        {item.activo && (
                          <button
                            type="button"
                            className="inv-action-btn inv-action-btn--delete"
                            onClick={() => eliminarInsumo(item)}
                            disabled={eliminandoId === item.id}
                          >
                            {eliminandoId === item.id ? "Eliminando..." : "Eliminar"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!cargando && filas.length > 0 && (
          <div className="inv-pagination">
            <div className="inv-page-size">
              <span>Items por pagina</span>
              <select
                value={itemsPorPagina}
                onChange={(e) => setItemsPorPagina(Number(e.target.value))}
                aria-label="Items por pagina"
              >
                {[10, 20, 50, 100].map((cantidad) => (
                  <option key={cantidad} value={cantidad}>{cantidad}</option>
                ))}
              </select>
            </div>

            <span className="inv-page-info">
              Mostrando {inicioPagina}-{finPagina} de {filas.length}
            </span>

            <div className="inv-page-controls" aria-label="Paginador de inventario">
              <button type="button" onClick={() => setPaginaActual(1)} disabled={paginaActual <= 1}>Primera</button>
              <button type="button" onClick={() => setPaginaActual((pagina) => Math.max(1, pagina - 1))} disabled={paginaActual <= 1}>Anterior</button>
              <span>Pagina {paginaActual} de {totalPaginas}</span>
              <button type="button" onClick={() => setPaginaActual((pagina) => Math.min(totalPaginas, pagina + 1))} disabled={paginaActual >= totalPaginas}>Siguiente</button>
              <button type="button" onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual >= totalPaginas}>Ultima</button>
            </div>
          </div>
        )}
      </section>

      {movimiento && (
        <div className="inv-modal-overlay" onMouseDown={cerrarMovimiento}>
          <div className="inv-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="inv-modal__header">
              <h2>{movimiento.tipo === "ENTRADA" ? "Ingresar stock" : "Registrar salida"}</h2>
              <button type="button" className="inv-modal__close" onClick={cerrarMovimiento} disabled={guardandoMovimiento} aria-label="Cerrar">×</button>
            </div>
            <div className="inv-modal__summary">
              <div><span>Insumo</span><strong>{movimiento.item.nombre}</strong></div>
              <div><span>Stock actual</span><strong>{formatCantidad(movimiento.item.stock_actual)} {movimiento.item.unidad_medida || "unidad"}</strong></div>
            </div>
            <form onSubmit={guardarMovimiento}>
              <label className="inv-modal__field">
                <span>{movimiento.tipo === "ENTRADA" ? "Cantidad ingresada" : "Cantidad retirada"}</span>
                <input type="number" min="0.001" step="0.001" value={cantidadMovimiento} onChange={(e) => setCantidadMovimiento(e.target.value)} autoFocus required />
              </label>
              {movimiento.tipo === "SALIDA" && (
                <label className="inv-modal__field">
                  <span>Retirado por</span>
                  <input type="text" value={retiradoPor} onChange={(e) => setRetiradoPor(e.target.value)} placeholder="Nombre de la persona" required />
                </label>
              )}
              <div className="inv-modal__result">
                Stock resultante: <strong>{formatCantidad(toNumber(movimiento.item.stock_actual) + (movimiento.tipo === "ENTRADA" ? 1 : -1) * (Number(cantidadMovimiento) || 0))} {movimiento.item.unidad_medida || "unidad"}</strong>
              </div>
              <div className="inv-modal__actions">
                <button type="button" className="inv-modal__cancel" onClick={cerrarMovimiento} disabled={guardandoMovimiento}>Cancelar</button>
                <button type="submit" className="inv-modal__submit" disabled={guardandoMovimiento}>{guardandoMovimiento ? "Guardando..." : movimiento.tipo === "ENTRADA" ? "Ingresar" : "Registrar salida"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historial && (
        <div className="inv-modal-overlay" onMouseDown={() => !cargandoHistorial && setHistorial(null)}>
          <div className="inv-modal inv-modal--history" onMouseDown={(e) => e.stopPropagation()}>
            <div className="inv-modal__header">
              <h2>Historial: {historial.item.nombre}</h2>
              <button type="button" className="inv-modal__close" onClick={() => setHistorial(null)} aria-label="Cerrar">×</button>
            </div>
            {cargandoHistorial ? <div className="inv-empty">Cargando historial...</div> : historial.movimientos.length === 0 ? <div className="inv-empty">No hay movimientos registrados.</div> : (
              <div className="inv-history-wrap">
                <table className="inv-history-table">
                  <thead><tr><th>Fecha</th><th>Tipo</th><th>Cantidad</th><th>Retirado por</th><th>Registrado por</th></tr></thead>
                  <tbody>{historial.movimientos.map((item) => {
                    const entrada = item.tipo === "ENTRADA";
                    return <tr key={item.id}>
                      <td>{formatearFecha(item.creado_en)}</td>
                      <td>{entrada ? "Entrada" : "Salida"}</td>
                      <td className={entrada ? "inv-history-positive" : "inv-history-negative"}>{entrada ? "+" : "-"}{formatCantidad(item.cantidad)} {item.unidad_medida || historial.item.unidad_medida || "unidad"}</td>
                      <td>{item.retirado_por || "—"}</td>
                      <td>{item.registrado_por || "—"}</td>
                    </tr>;
                  })}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {productoModal && (
        <div className="inv-modal-overlay" onMouseDown={() => !guardandoProducto && setProductoModal(false)}>
          <div className="inv-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="inv-modal__header">
              <h2>Agregar insumo</h2>
              <button type="button" className="inv-modal__close" onClick={() => setProductoModal(false)} disabled={guardandoProducto} aria-label="Cerrar">×</button>
            </div>
            <form onSubmit={guardarProducto}>
              <label className="inv-modal__field">
                <span>Nombre del insumo</span>
                <input type="text" value={productoForm.nombre} onChange={(e) => setProductoForm({ ...productoForm, nombre: e.target.value })} placeholder="Ej: Pollo" autoFocus required />
              </label>
              <div className="inv-modal__form-grid">
                <label className="inv-modal__field">
                  <span>Categoría</span>
                  <select value={productoForm.categoria_id} onChange={(e) => setProductoForm({ ...productoForm, categoria_id: e.target.value })}>
                    <option value="">Sin categoria</option>
                    {categoriasCatalogo.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                    ))}
                  </select>
                </label>
                <label className="inv-modal__field">
                  <span>Unidad</span>
                  <select value={productoForm.unidad_medida} onChange={(e) => setProductoForm({ ...productoForm, unidad_medida: e.target.value, unidad_personalizada: e.target.value === "otro" ? productoForm.unidad_personalizada : "" })}>
                    {unidades.map((unidad) => (
                      <option key={unidad} value={unidad}>{unidad}</option>
                    ))}
                  </select>
                </label>
                {productoForm.unidad_medida === "otro" && (
                  <label className="inv-modal__field">
                    <span>Otra unidad</span>
                    <input type="text" value={productoForm.unidad_personalizada} onChange={(e) => setProductoForm({ ...productoForm, unidad_personalizada: e.target.value })} placeholder="Ej: bandeja, lata, saco" required />
                  </label>
                )}
                <label className="inv-modal__field">
                  <span>Stock actual</span>
                  <input type="number" min="0" step="0.001" value={productoForm.stock_actual} onChange={(e) => setProductoForm({ ...productoForm, stock_actual: e.target.value })} placeholder="Ej: 5" />
                </label>
              </div>
              <label className="inv-modal__check"><input type="checkbox" checked={productoForm.activo} onChange={(e) => setProductoForm({ ...productoForm, activo: e.target.checked })} /> Insumo activo</label>
              <div className="inv-modal__actions">
                <button type="button" className="inv-modal__cancel" onClick={() => setProductoModal(false)} disabled={guardandoProducto}>Cancelar</button>
                <button type="submit" className="inv-modal__submit" disabled={guardandoProducto}>{guardandoProducto ? "Guardando..." : "Agregar insumo"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
